import React, { createContext, useContext, useState, useCallback } from 'react';
import type { Employee } from '@/types';

export type SetupStep = 1 | 2 | 3;

interface EmployeeSetupState {
  isActive: boolean;
  currentStep: SetupStep;
  employee: Employee | null;
  employeeType: 'employee' | 'contractor';
  completedSteps: number[];
}

interface EmployeeSetupContextValue extends EmployeeSetupState {
  startSetup: (type: 'employee' | 'contractor') => void;
  resumeSetup: (employee: Employee, step: SetupStep) => void;
  completeStep1: (employee: Employee) => void;
  completeStep2: () => void;
  completeStep3: () => void;
  cancelSetup: () => void;
}

const EmployeeSetupContext = createContext<EmployeeSetupContextValue | null>(null);

export const useEmployeeSetup = (): EmployeeSetupContextValue => {
  const ctx = useContext(EmployeeSetupContext);
  if (!ctx) throw new Error('useEmployeeSetup must be used within EmployeeSetupProvider');
  return ctx;
};

const INITIAL_STATE: EmployeeSetupState = {
  isActive: false,
  currentStep: 1,
  employee: null,
  employeeType: 'employee',
  completedSteps: [],
};

export const EmployeeSetupProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, setState] = useState<EmployeeSetupState>(INITIAL_STATE);

  const startSetup = useCallback((type: 'employee' | 'contractor') => {
    setState({ ...INITIAL_STATE, isActive: true, employeeType: type });
  }, []);

  const resumeSetup = useCallback((employee: Employee, step: SetupStep) => {
    const completedSteps = step === 2 ? [1] : step === 3 ? [1, 2] : [];
    const employeeType = employee.employment_type === 'contractor' ? 'contractor' : 'employee';
    setState({ isActive: true, currentStep: step, employee, employeeType, completedSteps });
  }, []);

  const completeStep1 = useCallback((employee: Employee) => {
    setState(prev => ({
      ...prev,
      currentStep: 2,
      employee,
      completedSteps: [1],
    }));
  }, []);

  const completeStep2 = useCallback(() => {
    setState(prev => ({
      ...prev,
      currentStep: 3,
      completedSteps: [1, 2],
    }));
  }, []);

  const completeStep3 = useCallback(() => {
    setState(prev => ({ ...prev, completedSteps: [1, 2, 3] }));
    setTimeout(() => setState(INITIAL_STATE), 2500);
  }, []);

  const cancelSetup = useCallback(() => {
    setState(INITIAL_STATE);
  }, []);

  return (
    <EmployeeSetupContext.Provider
      value={{ ...state, startSetup, resumeSetup, completeStep1, completeStep2, completeStep3, cancelSetup }}
    >
      {children}
    </EmployeeSetupContext.Provider>
  );
};
